import { CropIcon, EraserIcon, EyeIcon, EyeOffIcon, PipetteIcon, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { ColorSwatch } from '~/components/atoms/ColorSwatch';
import { HistoryGroupedSlider } from '~/components/atoms/HistoryGroupedSlider';
import { IconButton } from '~/components/atoms/IconButton';
import { Button } from '~/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { Switch } from '~/components/ui/switch';
import { detectBgColors } from '~/features/background/detect';
import { RegionListPanel } from '~/features/editor/RegionListPanel';
import { applyWatermarkInpaint } from '~/features/watermark/actions';
import { useStore } from '~/store';
import { Divider, GroupLabel, MorePopover, ToolbarRow, ToolSubPanel, UndoRedoGroup } from './parts';

export function MatteToolbar() {
  const drawMode = useStore((s) => s.editor.drawMode);
  return (
    <div className="relative w-full">
      <ToolbarRow>
        <UndoRedoGroup />
        <Divider />
        <BackgroundGroup />
        <Divider />
        <CropGroup />
        <Divider />
        <WatermarkGroup />
      </ToolbarRow>
      {drawMode === 'keyRegion' && (
        <ToolSubPanel label="Wand">
          <WandSubPanel />
        </ToolSubPanel>
      )}
      {drawMode === 'erase' && (
        <ToolSubPanel label="Eraser">
          <EraseSubPanel />
        </ToolSubPanel>
      )}
    </div>
  );
}

function CropGroup() {
  const drawMode = useStore((s) => s.editor.drawMode);
  const setDrawMode = useStore((s) => s.setDrawMode);
  return (
    <div className="flex items-center gap-1.5">
      <GroupLabel>Crop</GroupLabel>
      <IconButton
        label="Draw crop region"
        onClick={() => setDrawMode(drawMode === 'crop' ? 'none' : 'crop')}
        active={drawMode === 'crop'}
      >
        <CropIcon />
      </IconButton>
    </div>
  );
}

function WandSubPanel() {
  const wandTolerance = useStore((s) => s.background.wandTolerance);
  const setWandTolerance = useStore((s) => s.setWandTolerance);
  return (
    <>
      <span className="text-muted-foreground">Tolerance</span>
      <HistoryGroupedSlider
        aria-label="Wand tolerance"
        className="w-40"
        value={[wandTolerance]}
        min={0}
        max={255}
        step={1}
        onValueChange={([v]) => v != null && setWandTolerance(v)}
      />
      <span className="w-7 text-right tabular-nums text-muted-foreground">{wandTolerance}</span>
    </>
  );
}

function EraseSubPanel() {
  const brushRadius = useStore((s) => s.background.brushRadius);
  const brushSoftness = useStore((s) => s.background.brushSoftness);
  const setBrushRadius = useStore((s) => s.setBrushRadius);
  const setBrushSoftness = useStore((s) => s.setBrushSoftness);
  return (
    <>
      <span className="text-muted-foreground">Radius</span>
      <HistoryGroupedSlider
        aria-label="Brush radius"
        className="w-32"
        value={[brushRadius]}
        min={1}
        max={64}
        step={1}
        onValueChange={([v]) => v != null && setBrushRadius(v)}
      />
      <span className="w-6 text-right tabular-nums text-muted-foreground">{brushRadius}</span>
      <Divider />
      <span className="text-muted-foreground">Softness</span>
      <HistoryGroupedSlider
        aria-label="Brush softness"
        className="w-32"
        value={[brushSoftness]}
        min={0}
        max={1}
        step={0.05}
        onValueChange={([v]) => v != null && setBrushSoftness(v)}
      />
      <span className="w-8 text-right tabular-nums text-muted-foreground">
        {brushSoftness.toFixed(2)}
      </span>
    </>
  );
}

function BackgroundGroup() {
  const enabled = useStore((s) => s.background.enabled);
  const tolerance = useStore((s) => s.background.tolerance);
  const drawMode = useStore((s) => s.editor.drawMode);
  const setEnabled = useStore((s) => s.setBackgroundEnabled);
  const setTolerance = useStore((s) => s.setTolerance);
  const setDrawMode = useStore((s) => s.setDrawMode);

  const onToggleEnabled = (b: boolean) => {
    setEnabled(b);
    if (!b && drawMode === 'pickBg') setDrawMode('none');
  };

  return (
    <div className="flex items-center gap-1.5">
      <GroupLabel>BG</GroupLabel>
      <Switch
        aria-label="Toggle background removal"
        checked={enabled}
        onCheckedChange={onToggleEnabled}
      />
      <HistoryGroupedSlider
        aria-label="Background tolerance"
        className="w-24"
        value={[tolerance]}
        min={0}
        max={100}
        step={1}
        disabled={!enabled}
        onValueChange={([v]) => v != null && setTolerance(v)}
      />
      <span className="w-7 text-right tabular-nums text-muted-foreground">{tolerance}</span>
      <IconButton
        label="Pick background color from image (b)"
        onClick={() => setDrawMode(drawMode === 'pickBg' ? 'none' : 'pickBg')}
        active={drawMode === 'pickBg'}
        disabled={!enabled}
      >
        <PipetteIcon />
      </IconButton>
      <RegionGroup />
      <MorePopover label="Background">
        <BackgroundMore />
      </MorePopover>
    </div>
  );
}

function RegionGroup() {
  const drawMode = useStore((s) => s.editor.drawMode);
  const bgEnabled = useStore((s) => s.background.enabled);
  const regions = useStore((s) => s.background.regions);
  const showRegions = useStore((s) => s.editor.showRegions);
  const setDrawMode = useStore((s) => s.setDrawMode);
  const setShowRegions = useStore((s) => s.setShowRegions);

  const disabled = !bgEnabled;
  const erasing = drawMode === 'erase';
  return (
    <>
      <div className="relative">
        <IconButton
          label="Magic wand — flood-fill region (r)"
          onClick={() => setDrawMode(drawMode === 'keyRegion' ? 'none' : 'keyRegion')}
          active={drawMode === 'keyRegion'}
          disabled={disabled}
        >
          <Wand2 />
        </IconButton>
        {regions.length > 0 && (
          <span className="pointer-events-none absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium leading-none text-primary-foreground">
            {regions.length}
          </span>
        )}
      </div>
      <IconButton
        label="Soft eraser (e) — hold shift to un-erase"
        onClick={() => setDrawMode(erasing ? 'none' : 'erase')}
        active={erasing}
        disabled={disabled}
      >
        <EraserIcon />
      </IconButton>
      <IconButton
        label={showRegions ? 'Hide regions' : 'Show regions'}
        onClick={() => setShowRegions(!showRegions)}
        active={showRegions}
        disabled={regions.length === 0}
      >
        {showRegions ? <EyeIcon /> : <EyeOffIcon />}
      </IconButton>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-1.5 text-xs"
            disabled={disabled}
          >
            List
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-3">
          <RegionListPanel />
        </PopoverContent>
      </Popover>
    </>
  );
}

function BackgroundMore() {
  const hardEdge = useStore((s) => s.background.hardEdge);
  const bgColors = useStore((s) => s.background.bgColors);
  const erode = useStore((s) => s.background.erode);
  const feather = useStore((s) => s.background.feather);
  const despillStrength = useStore((s) => s.background.despillStrength);
  const despillRadius = useStore((s) => s.background.despillRadius);
  const floodFill = useStore((s) => s.background.floodFill);
  const maxEnclosedIsland = useStore((s) => s.background.maxEnclosedIsland);
  const setHardEdge = useStore((s) => s.setHardEdge);
  const setBgColors = useStore((s) => s.setBgColors);
  const setErode = useStore((s) => s.setErode);
  const setFeather = useStore((s) => s.setFeather);
  const setDespillStrength = useStore((s) => s.setDespillStrength);
  const setDespillRadius = useStore((s) => s.setDespillRadius);
  const setFloodFill = useStore((s) => s.setFloodFill);
  const setMaxEnclosedIsland = useStore((s) => s.setMaxEnclosedIsland);

  const reDetect = () => {
    const imageData = useStore.getState().source.imageData;
    if (!imageData) return;
    setBgColors(detectBgColors(imageData));
  };

  return (
    <>
      <div className="grid grid-cols-[max-content_minmax(0,1fr)_max-content] items-center gap-x-2 gap-y-1.5 text-xs">
        <span className="col-span-2">Hard edge (no soft alpha)</span>
        <Switch checked={hardEdge} onCheckedChange={setHardEdge} />

        <span className="col-span-2">Flood-fill (preserve interior holes)</span>
        <Switch checked={floodFill} onCheckedChange={setFloodFill} />

        <span>Max enclosed island</span>
        <HistoryGroupedSlider
          aria-label="Max enclosed island"
          value={[maxEnclosedIsland]}
          min={0}
          max={200}
          step={10}
          disabled={!floodFill}
          onValueChange={([v]) => v != null && setMaxEnclosedIsland(v)}
        />
        <span className="w-7 text-right tabular-nums text-muted-foreground">
          {maxEnclosedIsland}
        </span>

        <span>Edge shrink</span>
        <HistoryGroupedSlider
          aria-label="Edge shrink"
          value={[erode]}
          min={0}
          max={2}
          step={1}
          onValueChange={([v]) => v != null && setErode(v)}
        />
        <span className="w-5 text-right tabular-nums text-muted-foreground">{erode}</span>

        <span>Edge feather</span>
        <HistoryGroupedSlider
          aria-label="Edge feather"
          value={[feather]}
          min={0}
          max={1}
          step={0.5}
          disabled={erode === 0}
          onValueChange={([v]) => v != null && setFeather(v)}
        />
        <span className="w-5 text-right tabular-nums text-muted-foreground">{feather}</span>

        <span>Despill</span>
        <HistoryGroupedSlider
          aria-label="Despill strength"
          value={[despillStrength]}
          min={0}
          max={1}
          step={0.25}
          onValueChange={([v]) => v != null && setDespillStrength(v)}
        />
        <span className="w-5 text-right tabular-nums text-muted-foreground">{despillStrength}</span>

        <span>Despill radius</span>
        <HistoryGroupedSlider
          aria-label="Despill radius"
          value={[despillRadius]}
          min={0}
          max={3}
          step={1}
          disabled={despillStrength === 0}
          onValueChange={([v]) => v != null && setDespillRadius(v)}
        />
        <span className="w-5 text-right tabular-nums text-muted-foreground">{despillRadius}</span>
      </div>
      {bgColors.length > 0 && (
        <div className="space-y-1">
          {bgColors.map((c) => (
            <ColorSwatch
              key={c.rgb.join('-')}
              color={c}
              onRemove={() => setBgColors(bgColors.filter((x) => x !== c))}
            />
          ))}
        </div>
      )}
      <Button onClick={reDetect} variant="outline" size="sm" className="w-full">
        Re-detect colors
      </Button>
    </>
  );
}

function WatermarkGroup() {
  const enabled = useStore((s) => s.watermark.enabled);
  const region = useStore((s) => s.watermark.region);
  const setEnabled = useStore((s) => s.setWatermarkEnabled);
  const setDrawMode = useStore((s) => s.setDrawMode);

  return (
    <div className="flex items-center gap-1.5">
      <GroupLabel>WM</GroupLabel>
      <Switch
        aria-label="Toggle watermark removal"
        checked={enabled}
        onCheckedChange={setEnabled}
      />
      <IconButton
        label="Draw watermark region"
        onClick={() => setDrawMode('watermark')}
        active={!!region && enabled}
      >
        <EraserIcon />
      </IconButton>
      <MorePopover label="Watermark">
        <WatermarkMore />
      </MorePopover>
    </div>
  );
}

function WatermarkMore() {
  const enabled = useStore((s) => s.watermark.enabled);
  const region = useStore((s) => s.watermark.region);
  const useOpencv = useStore((s) => s.watermark.useOpencv);
  const setUseOpencv = useStore((s) => s.setUseOpencv);
  const [applying, setApplying] = useState(false);

  const onApply = async () => {
    setApplying(true);
    try {
      await applyWatermarkInpaint();
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between text-xs">
        <span>Use OpenCV inpaint (high quality)</span>
        <Switch checked={useOpencv} onCheckedChange={setUseOpencv} />
      </div>
      <Button
        onClick={() => {
          void onApply();
        }}
        disabled={!enabled || !region || applying}
        size="sm"
        variant="outline"
        className="w-full"
      >
        {applying ? 'Applying…' : 'Apply (bake into source)'}
      </Button>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Preview already shows the fill while toggled on. "Apply" bakes the result into the source
        image — useful before exporting or before further edits.
      </p>
    </>
  );
}
